import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/lib/cart";
import { mediaUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Minus, Plus, Trash2 } from "lucide-react";
import { useStoreBase } from "@/lib/store";
import { useTranslation } from "react-i18next";
import { money, symbolOf } from "@/lib/money";
import { storeOrdersPaused, storeOfflineMessage } from "@/lib/timeslotCheckout";

export default function StorefrontCart({ config }) {
  const { slug, base } = useStoreBase();
  const { t } = useTranslation();
  const nav = useNavigate();
  const { items, setQty, remove, subtotal } = useCart();

  const perProduct = Boolean(config?.per_product_shipping_active);
  const zonesOn = Boolean(config.shipping?.zones_enabled);
  const shippingKnown = !perProduct || items.every((it) => it.shipping_price != null);
  const shipping = (() => {
    if (perProduct) {
      if (!shippingKnown) return null;
      return items.reduce((n, it) => n + Number(it.shipping_price || 0) * Number(it.qty || 1), 0);
    }
    const s = config.shipping || {};
    if (zonesOn) return null;
    if (s.free_over && subtotal >= Number(s.free_over)) return 0;
    return Number(s.flat_rate || 0);
  })();
  const total = shipping == null ? subtotal : subtotal + shipping;

  if (items.length === 0) return (
    <div className="max-w-3xl mx-auto px-6 py-20 text-center">
      <div className="text-5xl mb-4">🛒</div>
      <h1 className="font-display text-2xl font-bold text-stone-900">{t("store.emptyCart")}</h1>
      <p className="text-stone-500 text-sm mt-2">{t("store.emptyCartHint")}</p>
      <Link to={`${base}/`}><Button className="mt-6 btn-store-primary rounded-full px-6" data-testid="cart-empty-continue">{t("store.continueShopping")}</Button></Link>
    </div>
  );

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
      <h1 className="font-display text-2xl sm:text-3xl font-bold text-stone-900">{t("store.yourCart")}</h1>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-2 bg-white rounded-2xl border border-stone-200/70 divide-y divide-stone-100">
          {items.map((it) => {
            const key = it.line_id || `${it.product_id}::${it.variant_id || ""}`;
            const sym = symbolOf(it);
            return (
            <div key={key} className="p-4 flex items-center gap-3 sm:gap-4" data-testid={`cart-item-${it.slug}`}>
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-lg bg-stone-100 overflow-hidden flex-shrink-0">
                {it.image && <img src={mediaUrl(it.image)} alt={it.name} className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-stone-900 truncate text-sm sm:text-base">{it.name}</div>
                <div className="font-mono-nums text-xs sm:text-sm text-stone-500 mt-0.5">{money(it.price, sym)}</div>
                {perProduct && it.shipping_price != null && (
                  <div className="text-[11px] text-stone-400" data-testid="cart-line-shipping">Shipping {money(Number(it.shipping_price) * Number(it.qty || 1), sym)}</div>
                )}
                {(it.custom_form_snapshot || []).length > 0 && (
                  <div className="text-[11px] text-stone-500 mt-1 space-y-0.5">
                    {it.custom_form_snapshot.map((f, i) => <div key={i}>{f.label}: {f.value}</div>)}
                  </div>
                )}
                {it.custom_print ? (
                  <div className="text-[11px] text-stone-500 mt-1" data-testid="cart-custom-print">Custom print{it.custom_print.text?.value ? `: “${it.custom_print.text.value}”` : ""}</div>
                ) : null}
              </div>
              {it.freeze_qty ? (
                <div className="text-sm font-mono-nums text-stone-600 px-2">{it.qty} pcs</div>
              ) : (
              <div className="inline-flex items-center rounded-full border border-stone-300 flex-shrink-0">
                <button onClick={() => setQty(it.product_id, it.variant_id, it.qty - 1, it.line_id)} className="w-8 h-8 flex items-center justify-center" data-testid={`cart-dec-${it.slug}`}><Minus className="w-3.5 h-3.5" /></button>
                <span className="w-8 text-center font-mono-nums text-sm">{it.qty}</span>
                <button onClick={() => setQty(it.product_id, it.variant_id, it.qty + 1, it.line_id)} className="w-8 h-8 flex items-center justify-center" data-testid={`cart-inc-${it.slug}`}><Plus className="w-3.5 h-3.5" /></button>
              </div>
              )}
              <div className="hidden sm:block w-20 text-right font-mono-nums font-semibold">{money(it.price * it.qty, sym)}</div>
              <button onClick={() => remove(it.product_id, it.variant_id, it.line_id)} className="text-red-600 hover:text-red-700" data-testid={`cart-remove-${it.slug}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          );})}
        </div>
        <div className="bg-white rounded-2xl border border-stone-200/70 p-5 h-fit">
          <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Summary</div>
          <div className="mt-4 space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-stone-500">Subtotal</span><span className="font-mono-nums">{money(subtotal, symbolOf(items[0]))}</span></div>
            <div className="flex justify-between"><span className="text-stone-500">Shipping</span><span className="font-mono-nums">{shipping == null ? "At checkout" : (shipping === 0 ? "Free" : money(shipping, symbolOf(items[0])))}</span></div>
            <div className="flex justify-between font-display font-bold text-lg pt-2 border-t border-stone-100 mt-2"><span>Total</span><span className="font-mono-nums">{money(total, symbolOf(items[0]))}</span></div>
          </div>
          <Button onClick={() => nav(`${base}/checkout`)} disabled={storeOrdersPaused(config)} data-testid="cart-checkout" className="w-full mt-5 btn-store-primary rounded-full h-11">{storeOrdersPaused(config) ? storeOfflineMessage(config) : t("store.checkout")}</Button>
        </div>
      </div>
    </div>
  );
}
